class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections?.[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class KmpCartPairs extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.track = this.querySelector('[data-kmp-pair-track]');
    this.cartProductIds = this.readCartProductIds();
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('change', this.handleChange.bind(this));
    this.loadProducts();
  }

  readCartProductIds() {
    try {
      return JSON.parse(this.dataset.cartProducts || '[]').map(Number);
    } catch (error) {
      return [];
    }
  }

  escapeHTML(value) {
    const element = document.createElement('div');
    element.textContent = value == null ? '' : String(value);
    return element.innerHTML;
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.dataset.currency || 'EUR',
      }).format(Number(cents) / 100);
    } catch (error) {
      return `${(Number(cents) / 100).toFixed(2)} ${this.dataset.currency || 'EUR'}`;
    }
  }

  imageURL(source) {
    if (!source) return '';
    const separator = source.includes('?') ? '&' : '?';
    return `${source}${separator}width=240`;
  }

  productCard(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const availableVariants = variants.filter((variant) => variant.available);
    const onlyDefault = variants.length === 1 && variants[0].title === 'Default Title';
    const initialVariant = onlyDefault && availableVariants.length ? availableVariants[0] : null;
    const rawImage = product.featured_image || product.images?.[0] || '';
    const image = typeof rawImage === 'string' ? rawImage : rawImage?.src || '';
    const currentPrice = initialVariant?.price ?? product.price;
    const comparePrice = initialVariant?.compare_at_price ?? product.compare_at_price;
    const hasComparePrice = Number(comparePrice) > Number(currentPrice);
    const actionLabel = !product.available
      ? this.dataset.soldLabel
      : initialVariant
        ? this.dataset.addLabel
        : this.dataset.chooseLabel;

    const options = variants
      .map((variant) => {
        const disabled = variant.available ? '' : ' disabled';
        const soldText = variant.available ? '' : ` · ${this.dataset.soldLabel}`;
        return `<option value="${variant.id}" data-price="${variant.price}" data-compare-price="${variant.compare_at_price || ''}"${disabled}>${this.escapeHTML(variant.title)}${this.escapeHTML(soldText)}</option>`;
      })
      .join('');

    return `
      <article class="kmp-cart-pair" role="listitem">
        ${image ? `<img class="kmp-cart-pair__image" src="${this.escapeHTML(this.imageURL(image))}" alt="${this.escapeHTML(product.title)}" loading="lazy" width="240" height="240">` : '<div class="kmp-cart-pair__image"></div>'}
        <div class="kmp-cart-pair__body">
          <h4 class="kmp-cart-pair__title">${this.escapeHTML(product.title)}</h4>
          <p class="kmp-cart-pair__price"><span data-kmp-pair-price>${this.formatMoney(currentPrice)}</span><s data-kmp-pair-compare${hasComparePrice ? '' : ' hidden'}>${hasComparePrice ? this.formatMoney(comparePrice) : ''}</s></p>
          ${!onlyDefault && product.available ? `<label class="visually-hidden" for="KmpPairVariant-${product.id}">${this.escapeHTML(this.dataset.chooseLabel)}</label><select class="kmp-cart-pair__select" id="KmpPairVariant-${product.id}" data-kmp-pair-select><option value="">${this.escapeHTML(this.dataset.chooseLabel)}</option>${options}</select>` : ''}
          <button class="kmp-cart-pair__action" type="button" data-kmp-pair-add data-variant="${initialVariant?.id || ''}" data-add-label="${this.escapeHTML(this.dataset.addLabel)}" data-choose-label="${this.escapeHTML(this.dataset.chooseLabel)}"${product.available ? '' : ' disabled'}>${this.escapeHTML(actionLabel)}</button>
        </div>
      </article>`;
  }

  async loadProducts() {
    if (!this.dataset.productId || !this.track) return;
    try {
      const url = `${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${encodeURIComponent(this.dataset.productId)}&limit=${encodeURIComponent(this.dataset.limit || 4)}&intent=complementary`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Complementary products could not load.');
      const data = await response.json();
      const products = (data.products || []).filter((product) => !this.cartProductIds.includes(Number(product.id)));
      if (!products.length) return;
      this.track.innerHTML = products.map((product) => this.productCard(product)).join('');
      this.hidden = false;
    } catch (error) {
      this.hidden = true;
    }
  }

  handleChange(event) {
    const select = event.target.closest('[data-kmp-pair-select]');
    if (!select || !this.contains(select)) return;
    const card = select.closest('.kmp-cart-pair');
    const action = card.querySelector('[data-kmp-pair-add]');
    const option = select.selectedOptions[0];
    const variantId = option?.value || '';
    action.dataset.variant = variantId;
    action.textContent = variantId ? action.dataset.addLabel : action.dataset.chooseLabel;

    if (variantId) {
      const price = card.querySelector('[data-kmp-pair-price]');
      const compare = card.querySelector('[data-kmp-pair-compare]');
      price.textContent = this.formatMoney(option.dataset.price);
      const hasCompare = Number(option.dataset.comparePrice) > Number(option.dataset.price);
      compare.textContent = hasCompare ? this.formatMoney(option.dataset.comparePrice) : '';
      compare.hidden = !hasCompare;
    }
  }

  async addVariant(button) {
    const variantId = button.dataset.variant;
    if (!variantId) {
      button.closest('.kmp-cart-pair')?.querySelector('[data-kmp-pair-select]')?.focus();
      return;
    }

    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Unable to add this item.');
      const drawer = document.querySelector('cart-drawer');
      if (drawer && data.sections) drawer.renderContents(data);
      else window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
    } catch (error) {
      button.textContent = error.message || 'Try again';
      window.setTimeout(() => {
        button.textContent = original;
        button.disabled = false;
      }, 2200);
    }
  }

  handleClick(event) {
    const arrow = event.target.closest('[data-kmp-pair-scroll]');
    if (arrow && this.contains(arrow)) {
      this.track?.scrollBy({ left: Number(arrow.dataset.kmpPairScroll) * this.track.clientWidth * 0.82, behavior: 'smooth' });
      return;
    }

    const addButton = event.target.closest('[data-kmp-pair-add]');
    if (addButton && this.contains(addButton)) this.addVariant(addButton);
  }
}

if (!customElements.get('kmp-cart-pairs')) customElements.define('kmp-cart-pairs', KmpCartPairs);